import { Gauge, Hourglass, Loader, AlertTriangle, Wrench } from "lucide-react";
import { Reveal, SectionHeading, waLink } from "./shared";

const SYMPTOMS = [
  { icon: Gauge, title: "Slow Performance", desc: "Takes forever to start or load" },
  { icon: Hourglass, title: "Frequent Freezing", desc: "Programs not responding" },
  { icon: Loader, title: "Long Load Times", desc: "Everything runs too slow" },
  { icon: AlertTriangle, title: "System Errors", desc: "Crashes and error messages" },
];

const REMEDY = [
  "Deep thermal repaste & full dust evacuation",
  "NVMe SSD migration — up to 10× faster",
  "OS optimization & bloatware purge",
];

export const SlowPC = () => (
  <section id="slow-pc" data-testid="slow-pc-section" className="relative py-24 sm:py-32 bg-void overflow-hidden">
    <div className="absolute -top-20 right-0 w-[28rem] h-[28rem] rounded-full bg-brand-crimson/10 blur-[140px] pointer-events-none" />
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
      <Reveal className="relative order-2 lg:order-1">
        <div className="relative clip-corner border border-brand-crimson/40 overflow-hidden shadow-[0_0_50px_rgba(239,68,68,0.15)]">
          <img
            src="/assets/collateral-1.jpg"
            alt="PC slow warning — AFZ can fix slow laptops"
            className="w-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-void/70 via-transparent to-transparent" />
        </div>
      </Reveal>

      <div className="order-1 lg:order-2">
        <SectionHeading
          eyebrow="Diagnostic Triage"
          title="PC Slow?"
          accent="We can fix it!"
          sub="You don't need to know the technical cause. A clear description of what the device is doing is enough to start."
        />
        <div className="mt-10 grid sm:grid-cols-2 gap-4">
          {SYMPTOMS.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.07}>
              <div className="group flex items-start gap-4 bg-card/80 border border-slate-800 rounded-xl p-5 hover:border-brand-crimson/60 transition-all duration-300 hover:shadow-[0_0_25px_rgba(239,68,68,0.15)]">
                <div className="w-11 h-11 shrink-0 rounded-full border-2 border-brand-crimson/60 bg-brand-crimson/10 flex items-center justify-center text-brand-crimson shadow-[0_0_16px_rgba(239,68,68,0.25)]">
                  <s.icon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold uppercase text-white">{s.title}</h3>
                  <p className="text-sm text-slate-400 mt-1">{s.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.3}>
          <div className="mt-8 bg-navy border border-brand-lime/30 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Wrench className="w-5 h-5 text-brand-lime" />
              <h3 className="font-display text-xl font-bold uppercase text-white">
                Optimize · Repair · <span className="text-brand-lime">Speed Up</span>
              </h3>
            </div>
            <ul className="space-y-2.5">
              {REMEDY.map((r, i) => (
                <li key={r} className="flex items-center gap-3 text-sm text-slate-300">
                  <span className="font-mono text-xs text-brand-lime">0{i + 1}</span> {r}
                </li>
              ))}
            </ul>
            <a
              data-testid="slowpc-fix-button"
              href={waLink("Hi AFZ Tech Solution, my PC is slow. I need it optimized and sped up.")}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 bg-brand-lime text-void font-display font-bold uppercase text-sm px-6 py-3 rounded-full hover:bg-brand-limedark transition-colors duration-300"
            >
              Get my PC running like new
            </a>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
